import { defineComponent as _defineComponent } from 'vue'
import type { Package, Tag } from '@/@types/graphql-types';
import type { TextRecommendationFragment } from '@/features/why-to-watch/graphql/fragments/TextRecommendation.fragment';
import type { TitleDetail } from '@/interfaces/title-details-graphql';

import { computed, ref } from 'vue';

import PackageRow from '@/components/modals/package-selection/PackageRow.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import BasicButton from '@/ui-components/BasicButton.vue';
import SearchBar from '@/components/search/SearchBar.vue';
import PictureComp from '@/components/picture/PictureComp.vue';
import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';

import { MonetizationType } from '@/@types/graphql-types';

import {
	CreateRecommendationDocument,
	CreateRecommendationMutation,
	CreateRecommendationMutationVariables,
} from '@/features/why-to-watch/graphql/mutations/CreateTextRecommendation.mutation';

import { toastError, toastSuccess } from '@/helpers/toast/toast-helper';
import { isMobile } from '@/helpers/ui-helper';
import { useMutation } from '@/helpers/composables/useApollo';
import { useLanguageStore } from '@/helpers/composables/useStores';
import {
	UpdateTextRecommendationDocument,
	UpdateTextRecommendationMutation,
	UpdateTextRecommendationMutationVariables,
} from '@/features/why-to-watch/graphql/mutations/UpdateTextRecommendation.mutation';
import { useI18n } from '@/helpers/composables/useI18n';


export default /*#__PURE__*/_defineComponent({
  __name: 'TextRecommendationModal',
  props: {
    title: null,
    formData: null,
    editMode: { type: Boolean, default: false }
  },
  emits: ["close"],
  setup(__props: any, { emit }: { emit: ({
	(e: 'close', display: boolean): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { title: TitleDetail, formData?: TextRecommendationFragment | null, editMode: boolean };

const BaseModalNew = () => import('@/components/modals/BaseModalNew.vue');





const { country, language } = useLanguageStore();

const { t, tc } = useI18n();

const dateInputRef = ref<HTMLInputElement | null>(null);

const providerSearch = ref<string>('');
const page = ref(1);
const displayProviderSearch = ref(false);
const isSubmitting = ref(false);

// Form Data
const id = ref<string>(props.formData?.id ?? '');
const headline = ref<string>(props.formData?.headline ?? '');
const body = ref<string>(props.formData?.body ?? '');
const formTags = ref<Tag[]>(props.formData?.tags ?? []);
const watchedOn = ref(props.formData?.watchedOn ?? null);
const watchedAt = ref<string>(props.formData?.watchedAt ?? '');

const titleName = computed(() => {
	const isSeason = props.title.__typename === 'Season' && !!props.title.show;
	return isSeason ? `${props.title.show.content.title} - ${props.title.content.title}` : props.title.content.title;
});

const providers = computed(() => {
	const nonCinemaProviders = props.title.offers.filter(offer => offer.monetizationType !== MonetizationType.Cinema);

	const uniqueProviderMap = new Map();
	nonCinemaProviders.forEach(offer => {
		if (!uniqueProviderMap.has(offer.package.packageId)) {
			uniqueProviderMap.set(offer.package.packageId, offer.package);
		}
	});

	const uniqueProviders = Array.from(uniqueProviderMap.values());

	// Add cinema provider to end of list if movie
	if (props.title.__typename === 'Movie') {
		uniqueProviders.push({
			packageId: 0,
			clearName: tc('WEBAPP_CINEMA'),
		});
	}

	return uniqueProviders.filter(p => p.clearName.toUpperCase().includes(providerSearch.value.toUpperCase()));
});

const titleHasTags = computed(() => (props.title.content.tags?.length ?? 0) > 0);

const tagsTitle = computed(() => {
	if (props.title.__typename === 'Show' || props.title.__typename === 'Season') {
		return tc('WEBAPP_SELECT_TAGS_Q_SHOW');
	} else {
		return tc('WEBAPP_SELECT_TAGS_Q_MOVIE');
	}
});

const selectedProviderImageUrl = computed(() => {
	if (watchedOn.value?.packageId === 0) {
		return 'https://static.justwatch.com/static/compile_jw/assets/jw-icon-badge.png';
	}

	return watchedOn.value?.icon
		? `${JW_CONFIG.IMAGESCALER_URL}${watchedOn.value?.icon.replace('{profile}', 's40')}`
		: '';
});

const selectedProviderName = computed(() => {
	if (watchedOn.value?.packageId === 0) {
		return tc('WEBAPP_CINEMA');
	}

	return watchedOn.value?.clearName;
});

const page1Validation = computed(() => {
	return !!(headline.value && body.value && body.value.length >= 60);
});

const page2Validation = computed(
	() => !!(watchedOn.value && watchedAt.value && new Date(watchedAt.value) <= new Date())
);

const submitValidation = computed(() => page1Validation.value && page2Validation.value);

const submitButtonText = computed(() =>
	props.editMode ? tc('WEBAPP_UPDATE_RECOMMENDATION') : tc('WEBAPP_ADD_RECOMMENDATION')
);

function toggleProviderSearch() {
	displayProviderSearch.value = !displayProviderSearch.value;
}

function providerClickHandler(provider: Package) {
	watchedOn.value = provider;
	toggleProviderSearch();
}

function handleDateClick() {
	dateInputRef.value?.focus();
	dateInputRef.value?.showPicker();
}

function handleNextClick() {
	if (page1Validation.value) page.value++;
}

function handlePreviousClick() {
	if (page.value > 1) page.value--;
}

const createRecommendation = useMutation<CreateRecommendationMutation, CreateRecommendationMutationVariables>(
	CreateRecommendationDocument,
	{
		refetchQueries: ['GetTextRecommendations'],
	}
);

const updateRecommendation = useMutation<UpdateTextRecommendationMutation, UpdateTextRecommendationMutationVariables>(
	UpdateTextRecommendationDocument,
	{
		refetchQueries: ['GetTextRecommendations'],
	}
);

function handleSubmit() {
	if (!submitValidation.value) return;
	isSubmitting.value = true;

	const formattedDate = new Date(`${watchedAt.value}T${new Date().toISOString().split('T')[1]}`).toISOString();

	const tagTechnicalNames = formTags.value.map(t => t.technicalName);

	if (props.editMode) {
		updateRecommendation.mutate({
			input: {
				id: id.value,
				headline: headline.value,
				body: body.value,
				tags: tagTechnicalNames,
				watchedAt: formattedDate,
				watchedOn: watchedOn.value?.packageId ?? 0,
			},
			country: country.value,
			language: language.value,
		});
	} else {
		createRecommendation.mutate({
			input: {
				textRecommendation: {
					headline: headline.value,
					body: body.value,
					tags: tagTechnicalNames,
					watchedAt: formattedDate,
					watchedOn: watchedOn.value?.packageId ?? 0,
				},
				jwEntityID: props.title.id,
				country: country.value,
			},
			country: country.value,
			language: language.value,
		});
	}

	isSubmitting.value = false;

	emit('close', false);
}

updateRecommendation.onDone(() => {
	toastSuccess({
		message: 'Your recommendation was successfully updated.',
		duration: 3000,
		position: isMobile() ? 'top' : 'bottom',
	});
});

updateRecommendation.onError(() => {
	toastError({
		message: 'An error occurred while updating your recommendation.',
		duration: 3000,
		position: isMobile() ? 'top' : 'bottom',
	});
});

createRecommendation.onDone(() => {
	toastSuccess({
		message: 'Your recommendation was successfully added.',
		duration: 3000,
		position: isMobile() ? 'top' : 'bottom',
	});
});

createRecommendation.onError(() => {
	toastError({
		message: 'An error occurred while adding your recommendation.',
		duration: 3000,
		position: isMobile() ? 'top' : 'bottom',
	});
});

const tags = computed(() => props.title.content.tags);

function handleTagChange(tag: Tag, event: Event) {
	const checkbox = event.target as HTMLInputElement;

	if (checkbox.checked) {
		if (formTags.value.length < 3) {
			formTags.value.push(tag);
		} else {
			checkbox.checked = false;
		}
	} else {
		formTags.value = formTags.value.filter(t => t.technicalName !== tag.technicalName);
	}
}

function isTagSelected(tag: Tag) {
	const selectedTags = formTags.value?.map(t => t.technicalName);
	return !!selectedTags?.includes(tag.technicalName);
}

return { __sfc: true,BaseModalNew, props, emit, country, language, t, tc, dateInputRef, providerSearch, page, displayProviderSearch, isSubmitting, id, headline, body, formTags, watchedOn, watchedAt, titleName, providers, titleHasTags, tagsTitle, selectedProviderImageUrl, selectedProviderName, page1Validation, page2Validation, submitValidation, submitButtonText, toggleProviderSearch, providerClickHandler, handleDateClick, handleNextClick, handlePreviousClick, createRecommendation, updateRecommendation, handleSubmit, tags, handleTagChange, isTagSelected, PackageRow, FontAwesomeIcon, BasicButton, SearchBar, PictureComp, HiddenHorizontalScrollbar }
}

})